/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, memo } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";

const BOT_NAME = "Aria"

const BOT_DESCRIPTION = `${BOT_NAME} is a warm, elegant, and intuitive fragrance expert with a feminine touch. She speaks softly yet confidently, making users feel pampered and understood. She has a passion for luxury perfumes and loves helping people find scents that express their unique personality.`

const PERSONALITY = `${BOT_DESCRIPTION} You are a friendly, knowledgeable virtual assistant representing The Perfume Shop, one of the UK’s leading fragrance retailers. For over 30 years, The Perfume Shop has been the go-to destination for both women's perfumes and men's aftershaves, offering a huge selection at affordable prices. You stock nearly 130 brands—including designer favourites like Hugo Boss, Gucci, Paco Rabanne, Mugler, and Marc Jacobs, as well as luxury names like Dior, Tom Ford, Hermès, Viktor & Rolf, and Maison Margiela.

You’re here to help customers find the perfect scent, answer questions, and guide them through services both online and in-store. You reflect the expertise of The Perfume Shop’s highly trained team (the most Fragrance Foundation graduates in the UK!) and offer smart tools like the Fragrance Finder to help customers decide what suits them best.

You can casually mention key perks like:

FREE standard delivery for VIP members

30-minute click & collect

A “Try Me” sample option for popular scents

The ability to buy now and pay later with Klarna

Rewards and sample selection at checkout for VIP members

Personalised services like gift wrap, engraving, and custom ribbons

A bottle recycling program that offers 15% off your next purchase

A wide range of refillable and vegan fragrance options

Speak in a warm, engaging, and helpful tone—like a trusted shop assistant who’s always ready to help, whether someone’s buying a gift, trying something new, or just browsing. Stay on-brand, reflect the quality and friendliness of The Perfume Shop, and always aim to make the fragrance shopping experience feel personal and enjoyable.`

const FAQS = {
  "I'm having trouble placing an order": "If using a mobile device or the app, try using the website to complete your order. If using the website, make sure you clear your cache and cookies. If this doesn’t work, try changing your browser. Still having trouble? Email our customer services team at onlineservices@theperfumeshop.com and include your browser/device details, any screenshots or photos, and use the same email you registered with. For more help, visit our live chat.",
  "Where is my order?": "Once your order has been dispatched, you’ll receive a dispatch email with a tracking link. You can then track the parcel directly with the courier. Orders are normally dispatched within a few working days. If you can’t find your email, check your junk/spam folders. Customer Service Live Chat: https://www.theperfumeshop.com/contact-us#liveChatSectionAnchor. Email: onlineservices@theperfumeshop.com",
  "Can I cancel or change my order?": "Once an order has been placed, unfortunately it cannot be cancelled or amended. You can return your order using our free returns service, or refuse delivery. The parcel will be returned to us and you will be refunded.",
  "Can I change my delivery address?": "Once an order has been placed, unfortunately it cannot be cancelled or amended.",
  "Do you offer a price match service?": "Unfortunately, we do not offer a price matching service. We do our best to remain competitive and offer great value to our customers.",
  "I haven't got my delivery confirmation": "Check your junk/spam folder in case the confirmation email is there. If you still haven’t received it and your order is late, contact Customer Services. Live Chat: https://www.theperfumeshop.com/contact-us#liveChatSectionAnchor. Email: onlineservices@theperfumeshop.com",
  "I didn't get an invoice in my parcel": "We’ve gone paperless, so you won’t receive any paperwork in your parcel. You’ll still get order confirmation and dispatch details via email.",
  "Are the prices on your website the same as in your stores?": "Our prices are the same online and instore unless it's an online exclusive offer.",
  "Do your prices change?": "In order to offer the customer the best deal possible at any given time, our prices do frequently change. In terms of orders however, the price that we charge will be the same as that quoted in your email confirmation.",
  "What's the difference between Aftershave, Eau de Toilette and Eau de Parfum?": "Quite simply it is the difference in the amount or concentration of the fragrance oils in a perfume. A higher concentration fragrance will last longer, and won't need as much applying. Sometimes the notes in an Eau de Toilette and Eau de Parfum of the same fragrance can vary slightly so if you are looking at trying something new, so don’t forget to try before you buy.",
  "Can I buy gift vouchers online?": "You are able to purchase gift cards and eGift cards online in various denominations.",
  "What services do you offer?": "We offer a range of services and add ons such as engraving, gift wrapping and monogramming. See here: /our-services",
  "Is it possible to get my product gift wrapped in store?": "In store you can get your item gift wrapped FREE of charge but there is a charge for personalised ribbon in the stores where it’s available. Please bear in mind that during peak times this service may not be available. Please ask our staff for more details in store."
}

const PERFUMES = [
  {
    "brand": "Gucci",
    "name": "Guilty Essence Pour Homme",
    "type": "Eau de Toilette Spray - 50ML",
    "price": "£55.20",
    "save": "£16.80",
    "rrp": "£72.00",
    "rating": 4.5,
    "image": "https://www.theperfumeshop.com/medias/sys_master/prd-images/hbf/h46/9274844512286/prd-front-1297051_172x172/gucci-igcfragrancefamilyvalue1-spritz-or-dab-directly-on-the-skin-and-onto-the-pulse-points-such-as-the-inside-of-the-wrists-the-chest-and-the-sides-of-the-neck-as-these-areas-retain-warmth-and-act-as-a-natural-diffuser-172x172.jpg",
    "link": "/gucci/guilty-essence-pour-homme/eau-de-toilette-spray/p/64580EDTJU"
  },
  {
    "brand": "Jean Paul Gaultier",
    "name": "Le Male Elixir",
    "type": "Parfum Spray - 75ML",
    "price": "£61.59",
    "save": "£25.41",
    "rrp": "£87.00",
    "rating": 4.6,
    "image": "https://www.theperfumeshop.com/medias/sys_master/prd-images/h4e/ha4/9234156847134/prd-front-1283198_172x172/jean-paul-gaultier-le-male-elixir-parfum-spray-172x172.jpg",
    "link": "/jean-paul-gaultier/le-male-elixir/parfum-spray/p/75980PRFJU"
  },
  {
    "brand": "Marc Jacobs",
    "name": "Daisy Wild",
    "type": "Eau de Parfum Spray - 50ML",
    "price": "£68.01",
    "save": "£16.99",
    "rrp": "£85.00",
    "rating": 4.7,
    "image": "https://www.theperfumeshop.com/medias/sys_master/prd-images/h29/h77/9218168455198/prd-front-1296708_172x172/marc-jacobs-daisy-wild-eau-de-parfum-spray-172x172.jpg",
    "link": "/marc-jacobs/daisy-wild/eau-de-parfum-spray/p/13180EDPJU?varSel=1296708"
  },
  {
    "brand": "Jean Paul Gaultier",
    "name": "Le Beau Paradise Garden",
    "type": "Eau de Parfum Spray - 125ML",
    "price": "£78.40",
    "save": "£32.60",
    "rrp": "£111.00",
    "rating": 4.5,
    "image": "https://www.theperfumeshop.com/medias/sys-master/prd-images/h61/h5a/9131931238430/prd-front-1295014_172x172/jean-paul-gaultier-le-beau-paradise-garden-eau-de-parfum-spray-172x172.jpg",
    "link": "/jean-paul-gaultier/le-beau-paradise-garden/eau-de-parfum-spray/p/63080EDPJU"
  },
  {
    "brand": "Gucci",
    "name": "Rush",
    "type": "Eau de Toilette Spray - 50ML",
    "price": "£31.99",
    "save": "£43.01",
    "rrp": "£75.00",
    "rating": 4.8,
    "image": "https://www.theperfumeshop.com/medias/sys_master/prd-images/h63/ha2/8835965288478/zoom-front-1007608_172x172/gucci-rush-eau-de-toilette-spray-172x172",
    "link": "/gucci/rush/eau-de-toilette-spray/p/21400EDTJU"
  },
  {
    "brand": "Rabanne",
    "name": "Million Gold",
    "type": "Eau de Parfum Spray - 100ML",
    "price": "£83.20",
    "save": "£20.80",
    "rrp": "£104.00",
    "rating": 4.3,
    "image": "https://www.theperfumeshop.com/medias/sys-master/prd-images/hd7/h70/9258444881950/prd-front-1300355_172x172/rabanne-1-million-gold-eau-de-parfum-spray-172x172.jpg",
    "link": "/rabanne/million-gold/eau-de-parfum-spray/p/77990EDPJU?varSel=1300355"
  },
  {
    "brand": "Burberry",
    "name": "Burberry Goddess",
    "type": "Eau de Parfum Refillable Spray - 50ML",
    "price": "£79.21",
    "save": "£19.79",
    "rrp": "£99.00",
    "rating": 4.8,
    "image": "https://www.theperfumeshop.com/medias/sys_master/prd-images/h77/hbd/8880025403422/zoom-front-1286257_172x172/burberry-burberry-goddess-eau-de-parfum-refillable-spray-172x172",
    "link": "/burberry/burberry-goddess/eau-de-parfum-refillable-spray/p/17880EDPJU?varSel=1286257"
  },
  {
    "brand": "Rabanne",
    "name": "Lady Million",
    "type": "Eau de Parfum Spray - 50ML",
    "price": "£71.20",
    "save": "£17.80",
    "rrp": "£89.00",
    "rating": 4.5,
    "image": "https://www.theperfumeshop.com/medias/sys-master/prd-images/hfc/ha9/9274794835998/prd-front-1100957_172x172/rabanne-lady-million-unknown-for-her-172x172.jpg",
    "link": "/rabanne/lady-million/eau-de-parfum-spray/p/31520EDPJU?varSel=1100957"
  },
  {
    "brand": "Jean Paul Gaultier",
    "name": "Gaultier Divine",
    "type": "Eau de Parfum Spray - 100ML",
    "price": "£99.20",
    "save": "£39.80",
    "rrp": "£139.00",
    "rating": 4.5,
    "image": "https://www.theperfumeshop.com/medias/sys-master/prd-images/hb6/hce/9259137007646/prd-front-1284969_172x172/jean-paul-gaultier-gaultier-divine-eau-de-parfum-spray-172x172.jpg",
    "link": "/jean-paul-gaultier/gaultier-divine/eau-de-parfum-spray/p/31960EDPJU?varSel=1284969"
  },
  {
    "brand": "HUGO BOSS",
    "name": "Boss The Scent For Him",
    "type": "Eau de Toilette Refillable Spray - 100ML",
    "price": "£39.99",
    "save": "£50.01",
    "rrp": "£90.00",
    "rating": 4.8,
    "image": "https://www.theperfumeshop.com/medias/sys-master/prd-images/h4d/h1f/9958955876382/prd-front-1307425_172x172/hugo-boss-boss-the-scent-for-him-eau-de-toilette-refillable-spray-172x172.jpg",
    "link": "/hugo-boss/boss-the-scent-for-him/eau-de-toilette-refillable-spray/p/64560EDTJU"
  }
]

const ORDERS = [
  {
    id: "ORD-001",
    customer: "Alice Thompson",
    perfume: "Gucci Rush",
    brand: "Gucci",
    store: "The Perfume Shop, Unit W9, St Nicholas Centre, Aberdeen",
    quantity: 1,
    price: "£31.99",
    status: "Ready for Pickup",
    pickupTime: "2025-05-21T16:30:00"
  },
  {
    id: "ORD-002",
    customer: "Mark Evans",
    perfume: "Le Male Elixir",
    brand: "Jean Paul Gaultier",
    store: "The Perfume Shop, Unit W9, St Nicholas Centre, Aberdeen",
    quantity: 2,
    price: "£123.18",
    status: "Processing",
    pickupTime: "2025-05-21T17:45:00"
  },
  {
    id: "ORD-003",
    customer: "Natalie Wright",
    perfume: "Daisy Wild",
    brand: "Marc Jacobs",
    store: "Union Square Shopping Centre, Guild Square, K5 The Atrium, Aberdeen",
    quantity: 1,
    price: "£68.01",
    status: "Collected",
    pickupTime: "2025-05-20T14:00:00"
  },
  {
    id: "ORD-004",
    customer: "Thomas Green",
    perfume: "Million Gold",
    brand: "Rabanne",
    store: "Kimberley Way, Unit 25, Ashford",
    quantity: 1,
    price: "£83.20",
    status: "Ready for Pickup",
    pickupTime: "2025-05-21T17:15:00"
  },
  {
    id: "ORD-005",
    customer: "Sophie Black",
    perfume: "Burberry Goddess",
    brand: "Burberry",
    store: "Fairhill Shopping Centre, Fairhill Roundabout, Ballymena",
    quantity: 1,
    price: "£79.21",
    status: "Processing",
    pickupTime: "2025-05-21T16:00:00"
  },
  {
    id: "ORD-006",
    customer: "Daniel Clarke",
    perfume: "Boss The Scent For Him",
    brand: "HUGO BOSS",
    store: "2 Fremlin Walk, Maidstone",
    quantity: 1,
    price: "£39.99",
    status: "Ready for Pickup",
    pickupTime: "2025-05-21T15:45:00"
  },
  {
    id: "ORD-007",
    customer: "Rebecca Morgan",
    perfume: "Lady Million",
    brand: "Rabanne",
    store: "Unit 10B, Castle Court, Royal Avenue, Belfast",
    quantity: 2,
    price: "£142.40",
    status: "Collected",
    pickupTime: "2025-05-20T18:30:00"
  },
  {
    id: "ORD-008",
    customer: "James Lee",
    perfume: "Le Beau Paradise Garden",
    brand: "Jean Paul Gaultier",
    store: "The Perfume Shop, Unit W9, St Nicholas Centre, Aberdeen",
    quantity: 1,
    price: "£78.40",
    status: "Processing",
    pickupTime: "2025-05-21T17:50:00"
  },
  {
    id: "ORD-009",
    customer: "Chloe Adams",
    perfume: "Gaultier Divine",
    brand: "Jean Paul Gaultier",
    store: "The Perfume Shop, Unit W9, St Nicholas Centre, Aberdeen",
    quantity: 1,
    price: "£99.20",
    status: "Ready for Pickup",
    pickupTime: "2025-05-21T17:10:00"
  },
  {
    id: "ORD-010",
    customer: "Harry Wilson",
    perfume: "Boss The Scent For Him",
    brand: "HUGO BOSS",
    store: "Unit 1, 41 William Street, Galashiels",
    quantity: 1,
    price: "£39.99",
    status: "Collected",
    pickupTime: "2025-05-20T13:45:00"
  }
];


const LOCATIONS = [
  {
    "location": "The Perfume Shop, Unit W9, St Nicholas Centre, Aberdeen",
    "closingTime": "18:00",
    "inventory": [
      "Gucci",
      "Jean Paul Gaultier",
      "Marc Jacobs",
      "HUGO BOSS"
    ]
  },
  {
    "location": "Union Square Shopping Centre, Guild Square, K5 The Atrium, Aberdeen",
    "closingTime": "19:00",
    "inventory": [
      "Marc Jacobs",
      "HUGO BOSS",
      "Rabanne"
    ]
  },
  {
    "location": "Kimberley Way, Unit 25, Ashford",
    "closingTime": "18:00",
    "inventory": [
      "Gucci",
      "Rabanne",
      "Burberry",
      "HUGO BOSS"
    ]
  },
  {
    "location": "Fairhill Shopping Centre, Fairhill Roundabout, Ballymena",
    "closingTime": "17:30",
    "inventory": [
      "Gucci",
      "Rabanne",
      "Burberry",
      "HUGO BOSS"
    ]
  },
  {
    "location": "Unit 4, The Long Walk Shopping Centre, Basingstoke",
    "closingTime": "18:00",
    "inventory": [
      "Gucci",
      "Rabanne",
      "Burberry",
      "HUGO BOSS"
    ]
  },
  {
    "location": "2 Fremlin Walk, Maidstone",
    "closingTime": "18:00",
    "inventory": [
      "Gucci",
      "HUGO BOSS"
    ]
  },
  {
    "location": "Unit 10B, Castle Court, Royal Avenue, Belfast",
    "closingTime": "21:00",
    "inventory": [
      "Gucci",
      "Rabanne",
      "HUGO BOSS"
    ]
  },
  {
    "location": "Unit 1, 41 William Street, Galashiels",
    "closingTime": "18:00",
    "inventory": [
      "HUGO BOSS"
    ]
  }
]


/* You are a perfume assistant by name Emma. I am including a static mapping of faqs for perfume ordering.
Please answer the questions based on the faqs and inventory appropriately. You should be able to assist with inventory questions as well but ensure you are answering in natural language when responding with json data in case of inventory question and you can refer the inventory list defined below. If the question is not in the faqs, redirect the user to the customer care.
*/

const PROMPT = `

${PERSONALITY}

Perfumes Inventory: ${JSON.stringify(PERFUMES)}

OrderDetails:
 description: these orders relate to perfume and locations so ensure you are able to answer questions about the orders and locations if asked.
 ${JSON.stringify(ORDERS)}

Locations with closing time and inventory available. For more details on product, refer to the perfumes inventory: ${JSON.stringify(LOCATIONS)}

FAQs: ${FAQS}
`


function AltairComponent() {
  const { client, setConfig } = useLiveAPIContext();

  useEffect(() => {
    setConfig({
      model: "models/gemini-2.0-flash-exp",
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            // text: 'You are wine bot by TheWineBase. You represent a wine store online and people can order wine online and get information about wine. Make up the answer about availability and delivery details.',
            text: PROMPT
          },
        ],
      },
    });
  }, [setConfig]);

  const embedRef = useRef<HTMLDivElement>(null);

  return <div className="vega-embed" ref={embedRef} />;
}

export const Altair = memo(AltairComponent);
